import {
	BasicButton,
	CreatePasswordValidation,
	Loading,
	SecondCustomInput,
} from "@/components";
import { updatePassword } from "@/redux/reducers/auth";
import { AuthWebviewSchema } from "@/schemas";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import { Link, createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useCallback, useEffect, useReducer, useState } from "react";
import { useDispatch } from "react-redux";

export const Route = createFileRoute("/new-password")({
	validateSearch: zodSearchValidator(AuthWebviewSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const dispatch = useDispatch();
	const navigate = Route.useNavigate();
	// initials form credentials
	const [formCredentials, setFormCredentials] = useReducer(
		(state: any, newState: any) => ({ ...state, ...newState }),
		{
			password: "",
			password_confirmation: "",
		},
	);
	// password status
	const [passwordStatus, setPasswordStatus] = useState({
		length: false,
		lowercase: false,
		uppercase: false,
		digit: false,
		special: false,
	});
	// password visible
	const [passwordVisible, setPasswordVisible] = useState(false);
	const [confirmationVisible, setConfirmationVisible] = useState(false);
	// button disable
	const [buttonDisable, setButtonDisable] = useState(true);
	const [loading, setLoading] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		// check password status
		setPasswordStatus(() => ({
			length: formCredentials.password.length >= 9,
			lowercase: /[a-z]/.test(formCredentials.password),
			uppercase: /[A-Z]/.test(formCredentials.password),
			digit: /[0-9]/.test(formCredentials.password),
			special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(
				formCredentials.password,
			),
		}));
		// check input value
		setButtonDisable(
			!(
				formCredentials.password &&
				formCredentials.password_confirmation &&
				Object.values(passwordStatus).every((status) => status)
			),
		);
	}, [formCredentials, passwordStatus, buttonDisable]);

	const handleSubmit = useCallback(() => {
		setLoading(true);
		dispatch(
			updatePassword({
				password: formCredentials.password,
				password_confirmation: formCredentials.password_confirmation,
				callback: () => {
					setLoading(false);
					navigate({
						to: "/success",
					});
				},
			}),
		);
	}, [dispatch, navigate, formCredentials]);

	return (
		<>
			<div className="container wrapper">
				<AppBar position="fixed" className="!bg-white !shadow-none">
					<Toolbar variant="dense" className="wrapper w-full h-14">
						<Link to={"/forgot-password"}>
							<IconButton
								edge="start"
								color="inherit"
								aria-label="backspace"
								className="mr-2 text-2xl"
							>
								<KeyboardBackspaceIcon className="text-dark" />
							</IconButton>
						</Link>
					</Toolbar>
				</AppBar>
				<Box className="flex flex-col items-center mt-12">
					<h1 className="w-full text-[28px] font-semibold py-3 mb-4">
						Enter new password
					</h1>
					<Box className="w-full">
						<SecondCustomInput
							type="password"
							label="Password"
							name="password"
							value={formCredentials.password}
							onChange={(e) => setFormCredentials({ password: e.target.value })}
							onToggleVisibility={() => setPasswordVisible(!passwordVisible)}
							showPassword={passwordVisible}
							startIcon={<LockOutlinedIcon />}
						/>
						<CreatePasswordValidation value={formCredentials.password} />
						<SecondCustomInput
							type="password"
							label="Confirm"
							name="confirm"
							value={formCredentials.password_confirmation}
							onChange={(e) =>
								setFormCredentials({ password_confirmation: e.target.value })
							}
							onToggleVisibility={() =>
								setConfirmationVisible(!confirmationVisible)
							}
							showPassword={confirmationVisible}
							startIcon={<LockOutlinedIcon />}
						/>
						<BasicButton
							variant="contained"
							color="secondary"
							onClick={handleSubmit}
							className="w-full !h-14 !text-base !mt-8 !shadow-none"
						>
							Confirm
						</BasicButton>
					</Box>
				</Box>
			</div>
			<Loading status={loading} />
		</>
	);
}
